/* eslint-disable no-shadow */
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import Box from '@mui/material/Box';
import {
  fixedWidgets,
  isDownloadWidget,
  isSpinradWidget,
  isStoryWidget,
} from '../../utils/fixedWidgets';
import { Widget } from '../../pages/UserGroupsPage';
import { AddUrlDownloads, DownloadItem } from '../addUrlDownloads';
import { Container, TreeViewContainer, WidgetsDetails } from './styles';
import EBTreeView from '../EBTreeView';
import { AddImagesStory, StoryUnit } from '../addImagesStory';
import { AddSpinradValues } from '../addSpinradValues';

enum EnumWidgetType {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

interface Props {
  widgetsTree: Widget;
  setWidgetsTree: Function;
  usergroupId: number;
  setWidgetsToDelete: Function;
  addFileIdsToDeleteList: any;
}

const WidgetsBox: React.FC<Props> = ({
  widgetsTree,
  setWidgetsTree,
  setWidgetsToDelete,
  usergroupId,
  addFileIdsToDeleteList,
}) => {
  const [downloadItemList, setDownloadItemList] = useState<DownloadItem[]>([]);
  const [downloadItemUrlsToDelete, setDownloadItemUrlsToDelete] = useState<
    string[]
  >([]);

  const [storiesList, setStoriesList] = useState<StoryUnit[]>([]);
  const [spinradValuesList, setSpinradValuesList] = useState<string[]>([]);

  const [selectedWidget, setSelectedWidget] = useState<Widget>();
  const [widgetName, setWidgetName] = useState<string>('');
  const [widgetUrl, setWidgetUrl] = useState<string>('');
  const [widgetUrlImage, setWidgetUrlImage] = useState<string>('');
  const [widgetType, setWidgetType] = useState<string>(EnumWidgetType.EXTERNAL);
  const [description, setDescription] = useState<string>('');
  const [required, setRequired] = useState<any>({});
  const [showWidgetDetails, setShowWidgetDetails] = useState<boolean>(false);
  const [widgetParent, setWidgetParent] = useState<any>();
  const [tempId, setTempId] = useState<number>(-1);

  const hasRequiredFields = useCallback((): boolean => {
    const requiredObj: any = {};
    if (widgetName.trim() === '') {
      requiredObj.widgetName = { error: true, message: 'required' };
    } else if (widgetName.trim().length > 20) {
      requiredObj.widgetName = {
        error: true,
        message: 'the name should have at maximun 20 characters',
      };
    }

    if (Object.keys(requiredObj).length > 0) {
      setRequired(requiredObj);
      return true;
    }
    return false;
  }, [widgetName]);

  const insertWidget = useCallback((node: any, widgetToInsert: any) => {
    if (node.widgetId === widgetToInsert.parentId) {
      const newNode = { ...node };

      if (newNode.children) {
        newNode.children.push(widgetToInsert);
      } else {
        newNode.children = [widgetToInsert];
      }

      return newNode;
    }

    const newItems = node.children.map((node: any) => {
      const newNode = { ...node };

      if (
        node.widgetId === widgetToInsert.parentId ||
        node.tempId === widgetToInsert.parentId
      ) {
        if (node.children) {
          newNode.children.push(widgetToInsert);
        } else {
          newNode.children = [widgetToInsert];
        }
      } else if (node.children) {
        return insertWidget(node, widgetToInsert);
      }

      return newNode;
    });

    return { ...node, children: newItems };
  }, []);

  const deleteWidget = useCallback((node: any, widgetToDelete: any) => {
    const newChildren = node.children?.map((no: any) => {
      return deleteWidget(no, widgetToDelete);
    });

    const filtered = newChildren?.filter(
      (item: any) => item.widgetId !== widgetToDelete.widgetId,
    );

    return { ...node, children: filtered };
  }, []);

  const editWidget = useCallback((node: any, widgetToEdit: any) => {
    const newItems = node.children?.map((item: any) => {
      if (item.widgetId === widgetToEdit.widgetId) {
        return widgetToEdit;
      }

      return editWidget(item, widgetToEdit);
    });

    return { ...node, children: newItems };
  }, []);

  const cleanWidgetBoxDetails = useCallback(() => {
    setWidgetName('');
    setWidgetUrl('');
    setWidgetUrlImage('');
    setDescription('');
    setDownloadItemList([]);
    setDownloadItemUrlsToDelete([]);
    setStoriesList([]);
    setSpinradValuesList([]);
    setSelectedWidget(undefined);
  }, []);

  const handleEditWidget = useCallback(() => {
    const widgetToEdit: any = {
      ...selectedWidget,
      name: widgetName,
      url: widgetUrl,
      urlImage: widgetUrlImage,
      description,
      type: widgetType,
      downloadItens: downloadItemList,
      stories: storiesList,
      spinradValues: spinradValuesList,
    };

    if (!isDownloadWidget(widgetToEdit)) {
      if (widgetToEdit.downloadItens.length > 0) {
        addFileIdsToDeleteList(
          widgetToEdit?.downloadItens?.map(
            (downloadItem: DownloadItem) => downloadItem.fileId,
          ) ?? [],
        );
      }

      delete widgetToEdit.downloadItems;
    }

    if (!hasRequiredFields()) {
      const objWidgets = {
        widgetName: 'widgets list',
        widgetId: widgetsTree.widgetId,
        children: widgetsTree.children,
      };

      const updatedTree = editWidget(objWidgets, widgetToEdit);
      setWidgetsTree(updatedTree);

      setShowWidgetDetails(false);
      cleanWidgetBoxDetails();
      setRequired({});
    }
  }, [
    addFileIdsToDeleteList,
    cleanWidgetBoxDetails,
    description,
    downloadItemList,
    editWidget,
    hasRequiredFields,
    selectedWidget,
    setWidgetsTree,
    spinradValuesList,
    storiesList,
    widgetName,
    widgetType,
    widgetUrl,
    widgetUrlImage,
    widgetsTree.children,
    widgetsTree.widgetId,
  ]);

  const handleInsertWidget = useCallback(() => {
    const widgetToInsert: any = {
      name: widgetName,
      tempId,
      parentId: widgetParent.widgetId
        ? widgetParent.widgetId
        : widgetParent.tempId,
      url: widgetUrl,
      urlImage: widgetUrlImage,
      description,
      type: widgetType,
      downloadItens: downloadItemList,
      stories: storiesList,
      spinradValues: spinradValuesList,
    };

    // this tempId is a solution when the user add more then one widgets before saving and try to drag and drop them over the tree.
    // when the user create the widget, the widget doesn't have id yet, this tempId is to help during drag and drop before saving
    setTempId(prev => prev - 1);

    if (widgetToInsert.url !== '/downloads') {
      if (widgetToInsert.downloadItens.length > 0) {
        addFileIdsToDeleteList(
          widgetToInsert?.downloadItens?.map(
            (downloadItem: DownloadItem) => downloadItem.fileId,
          ) ?? [],
        );
      }

      delete widgetToInsert.downloadItens;
    }

    if (!hasRequiredFields()) {
      const objWidgets = {
        widgetName: 'widgets list',
        widgetId: widgetsTree.widgetId,
        children: widgetsTree.children,
      };

      const updatedTree = insertWidget(objWidgets, widgetToInsert);

      setWidgetsTree(updatedTree);

      setShowWidgetDetails(false);
      cleanWidgetBoxDetails();
      setRequired({});
    }
  }, [
    addFileIdsToDeleteList,
    cleanWidgetBoxDetails,
    description,
    downloadItemList,
    hasRequiredFields,
    insertWidget,
    setWidgetsTree,
    spinradValuesList,
    storiesList,
    tempId,
    widgetName,
    widgetParent,
    widgetType,
    widgetUrl,
    widgetUrlImage,
    widgetsTree.children,
    widgetsTree.widgetId,
  ]);

  const handleDeleteWidget = useCallback(() => {
    addFileIdsToDeleteList(
      selectedWidget?.downloadItens?.map(downloadItem => downloadItem.fileId) ??
        [],
    );

    setWidgetsToDelete((prev: any) => [...prev, selectedWidget]);

    const objWidgets = {
      widgetName: 'widgets list',
      widgetId: widgetsTree.widgetId,
      children: widgetsTree.children,
    };

    const updatedTree = deleteWidget(objWidgets, selectedWidget);

    setWidgetsTree(updatedTree);

    setShowWidgetDetails(false);
    cleanWidgetBoxDetails();
  }, [
    addFileIdsToDeleteList,
    selectedWidget,
    setWidgetsToDelete,
    widgetsTree.widgetId,
    widgetsTree.children,
    deleteWidget,
    setWidgetsTree,
    cleanWidgetBoxDetails,
  ]);

  const openWidgetForm = useCallback(
    (node: any, isAdding: boolean) => {
      if (isAdding) {
        cleanWidgetBoxDetails();
        setWidgetParent(node);
      } else {
        setSelectedWidget(node);
      }

      setShowWidgetDetails(true);
    },
    [cleanWidgetBoxDetails],
  );

  const handleCancel = useCallback(() => {
    cleanWidgetBoxDetails();
    setShowWidgetDetails(false);
  }, [cleanWidgetBoxDetails]);

  useEffect(() => {
    if (selectedWidget) {
      setShowWidgetDetails(true);
      setWidgetName(selectedWidget.name);
      setWidgetUrl(selectedWidget.url);
      setWidgetUrlImage(selectedWidget.urlImage);
      setWidgetType(selectedWidget.type);
      setDescription(selectedWidget.description);
      setDownloadItemList(selectedWidget.downloadItens ?? []);
      setStoriesList(selectedWidget.stories ?? []);
      setSpinradValuesList(selectedWidget.spinradValues ?? []);
    }
  }, [selectedWidget]);

  const showInternalWidgetSelect = widgetType === EnumWidgetType.INTERNAL;
  const internalWidgetType = showInternalWidgetSelect
    ? fixedWidgets.find(x => x.url === widgetUrl)
    : '';
  const showAddDownloadWidget = isDownloadWidget({
    type: widgetType,
    url: widgetUrl,
  });

  const showAddStoryWidget = isStoryWidget({
    type: widgetType,
    url: widgetUrl,
  });

  const showAddSpinradWidget = isSpinradWidget({
    type: widgetType,
    url: widgetUrl,
  });

  return (
    <>
      <Container>
        <TreeViewContainer>
          <EBTreeView
            data={widgetsTree}
            setData={setWidgetsTree}
            onAddItem={openWidgetForm}
            onEditItem={openWidgetForm}
          ></EBTreeView>
        </TreeViewContainer>
        <WidgetsDetails>
          {showWidgetDetails && (
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <div
                style={{
                  width: '94%',
                  alignSelf: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <h3
                  style={{
                    margin: 'unset',
                    marginBottom: '1vh',
                    marginTop: '0.5vh',
                    paddingLeft: '3px',
                  }}
                >
                  {selectedWidget ? selectedWidget.name : 'New widget'}
                </h3>
              </div>
              <FormControl
                fullWidth
                style={{
                  width: '94%',
                  alignSelf: 'center',
                  margin: '8px 0 4px 0',
                }}
              >
                <InputLabel id="widgetTypeSelect-label">Type</InputLabel>
                <Select
                  labelId="widgetTypeSelect-label"
                  id="widgetTypeSelect"
                  name="widgetType"
                  label="Type"
                  value={widgetType}
                  onChange={event => {
                    setWidgetUrl('');
                    setWidgetType(event.target.value);
                  }}
                >
                  <MenuItem key={0} value={EnumWidgetType.INTERNAL}>
                    Internal
                  </MenuItem>
                  <MenuItem key={1} value={EnumWidgetType.EXTERNAL}>
                    External
                  </MenuItem>
                </Select>
              </FormControl>
              {showInternalWidgetSelect && (
                <FormControl
                  fullWidth
                  style={{
                    width: '94%',
                    alignSelf: 'center',
                    margin: '8px 0 4px 0',
                  }}
                >
                  <InputLabel id="selectInternalWidget-label">
                    Internal Widget
                  </InputLabel>
                  <Select
                    value={JSON.stringify(internalWidgetType)}
                    error={!!required.internalWidget}
                    labelId="selectInternalWidget-label"
                    label="Internal Widget"
                    id="selectInternalWidget"
                    name="internalWidget"
                    onChange={event => {
                      setWidgetUrl(
                        JSON.parse(event.target.value as string).url,
                      );
                      setRequired((prev: any) => {
                        const newObj = { ...prev };
                        delete newObj.internalWidget;
                        return newObj;
                      });
                    }}
                  >
                    {fixedWidgets.map(internalWidget => {
                      return (
                        <MenuItem
                          key={internalWidget.name}
                          value={JSON.stringify(internalWidget)}
                        >
                          {internalWidget.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
              {showAddDownloadWidget && (
                <div
                  style={{
                    width: '94%',
                    alignSelf: 'center',
                    margin: '10px 0',
                    backgroundColor: 'rgb(194, 194, 194)',
                  }}
                >
                  <AddUrlDownloads
                    downloadItemsList={downloadItemList}
                    setDownloadItemList={setDownloadItemList}
                    downloadItemsUrlToDelete={downloadItemUrlsToDelete}
                    setDownloadItemsUrlToDelete={setDownloadItemUrlsToDelete}
                    isEdit={false}
                    usergroupId={usergroupId}
                  ></AddUrlDownloads>
                </div>
              )}
              {showAddStoryWidget && (
                <div
                  style={{
                    width: '94%',
                    alignSelf: 'center',
                    margin: '10px 0',
                  }}
                >
                  <AddImagesStory
                    storiesList={storiesList}
                    setStoriesList={setStoriesList}
                  />
                </div>
              )}
              {showAddSpinradWidget && (
                <div
                  style={{
                    width: '94%',
                    alignSelf: 'center',
                    margin: '10px 0',
                  }}
                >
                  <AddSpinradValues
                    valuesList={spinradValuesList}
                    setValuesList={setSpinradValuesList}
                  />
                </div>
              )}
              <TextField
                margin="dense"
                id="widgetName"
                name="widgetName"
                value={widgetName}
                onChange={event => {
                  setWidgetName(event.target.value);
                  if (event.target.value?.trim()) {
                    setRequired((prev: any) => {
                      const newObj = { ...prev };
                      delete newObj.widgetName;
                      return newObj;
                    });
                  }
                }}
                label="Widget Name"
                type="text"
                variant="outlined"
                error={!!required.widgetName}
                helperText={required.widgetName?.message}
                style={{ width: '94%', alignSelf: 'center' }}
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                margin="dense"
                id="widgetUrl"
                name="widgetUrl"
                value={widgetUrl}
                onChange={event => {
                  setWidgetUrl(event.target.value);
                  setRequired((prev: any) => {
                    const newObj = { ...prev };
                    delete newObj.widgetUrl;
                    return newObj;
                  });
                }}
                label="URL"
                type="text"
                variant="outlined"
                error={!!required.widgetUrl}
                helperText={required.widgetUrl?.message}
                style={{ width: '94%', alignSelf: 'center' }}
                disabled={widgetType === EnumWidgetType.INTERNAL}
              />
              <TextField
                margin="dense"
                id="imageUrl"
                name="imageUrl"
                value={widgetUrlImage}
                onChange={event => setWidgetUrlImage(event.target.value)}
                label="Image URL"
                type="text"
                variant="outlined"
                error={!!required.imageUrl}
                helperText={required.imageUrl?.message}
                style={{ width: '94%', alignSelf: 'center' }}
              />
              <TextField
                margin="dense"
                id="description"
                name="description"
                value={description}
                onChange={event => setDescription(event.target.value)}
                label="Description"
                type="text"
                variant="outlined"
                error={!!required.description}
                helperText={required.description?.message}
                style={{ width: '94%', alignSelf: 'center' }}
                inputProps={{ maxLength: 250 }}
              />

              {selectedWidget ? (
                <div
                  style={{
                    width: '94%',
                    height: '7%',
                    alignSelf: 'center',
                    paddingTop: '7px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex' }}>
                    <Button
                      style={{ background: '#ff3737' }}
                      onClick={handleDeleteWidget}
                      variant="contained"
                    >
                      Delete widget
                    </Button>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button onClick={handleCancel} variant="contained">
                      Cancel
                    </Button>
                    <Button onClick={handleEditWidget} variant="contained">
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: '94%',
                    height: '7%',
                    alignSelf: 'center',
                    paddingTop: '7px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button onClick={handleCancel} variant="contained">
                      Cancel
                    </Button>
                    <Button onClick={handleInsertWidget} variant="contained">
                      Add widget
                    </Button>
                  </div>
                </div>
              )}
            </Box>
          )}
        </WidgetsDetails>
      </Container>
    </>
  );
};

export default WidgetsBox;
